import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const { loginToken, loginTokenCoUser } = getState();
  const token = loginToken?.response?.token || loginTokenCoUser?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

export const fetchCannedMessages = createAsyncThunk(
  "cannedMessages/fetch",
  async ({ businessId, groupId }, { getState, rejectWithValue }) => {
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const params = groupId ? `?group_id=${encodeURIComponent(groupId)}` : "";
      const response = await axios.get(
        `${ROOT_URL}/canned-messages/${businessId}${params}`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const createCannedMessage = createAsyncThunk(
  "cannedMessages/create",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/canned-messages`,
        payload,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const updateCannedMessage = createAsyncThunk(
  "cannedMessages/update",
  async ({ id, payload }, { getState, rejectWithValue }) => {
    if (!id) {
      return rejectWithValue({ message: "Missing canned response id" });
    }
    try {
      const response = await axios.put(
        `${ROOT_URL}/canned-messages/${id}`,
        payload,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const deleteCannedMessage = createAsyncThunk(
  "cannedMessages/delete",
  async (id, { getState, rejectWithValue }) => {
    if (!id) {
      return rejectWithValue({ message: "Missing canned response id" });
    }
    try {
      const response = await axios.delete(
        `${ROOT_URL}/canned-messages/${id}`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

const initialState = {
  list: [],
  loading: false,
  saving: false,
  error: null,
};

const cannedMessagesApi = createSlice({
  name: "cannedMessages",
  initialState,
  reducers: {
    clearCannedMessages: () => initialState,
    clearCannedMessagesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCannedMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCannedMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.list = Array.isArray(action.payload?.data) ? action.payload.data : [];
      })
      .addCase(fetchCannedMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.list = [];
      })
      .addCase(createCannedMessage.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createCannedMessage.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createCannedMessage.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateCannedMessage.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateCannedMessage.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateCannedMessage.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { clearCannedMessages, clearCannedMessagesError } = cannedMessagesApi.actions;
export default cannedMessagesApi.reducer;
